import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FleetGrid } from '../../shared/sso-workspace/fleet-grid'
import { agoText, utcText } from '../../shared/date/local-time'
import { useMinuteClock } from '../../shared/date/use-minute-clock'
import { useIsMobile } from '../../shared/hooks/use-is-mobile'
import { Floating } from '../../shared/component/floating'
import { IconCircleExclamation, IconMenu } from '../../shared/icon/icon'
import { logoFor } from './format'
import '../../shared/sso-workspace/sso-workspace.scss'
import './lms-admin.scss'

// The LMS pages' lists are the table-builder's AG Grid (TableAgClient inside
// FleetGrid), like every other admin list. FleetGrid's card, pills and buttons
// read the workspace's --sw-* tokens, which only a .sw-workspace element
// carries; the LMS pages are not inside a FleetPage, so each grid sits in one.
// menu: the page's actions for the list as the card menu's items (owner
// 2026-10-29, CLAUDE.md "List page layout"): create/add items are `pin`ned
// (`keepOnMobile` on the primary), the rest go in the ⋮ with Table settings.
// after: what belongs under the grid, outside it (a cursor's "Load more").
export const LmsGrid = ({ menu, after, ...props }) =>
  <div className="sw-workspace lms-grid">
    <FleetGrid menu={menu} {...props} />
    {after && <div className="lms-grid-after">{after}</div>}
  </div>

// What a grid says when its list could not be read and nothing has loaded yet
// (the page's banner has the reason): never its "nothing yet" copy, which would
// claim the list is empty. When rows are already there, a failed reload keeps
// them and only the banner speaks.
export const FAILED_STATE = {
  Icon: IconCircleExclamation,
  title: "Couldn't load this list",
  message: 'Use Reload below or try again shortly.'
}

// The rows of an LMS grid, and how they are read. Every grid reads its list the
// same way:
//   - rows: undefined while the first read is out, null when nothing has loaded
//     (the grid then says FAILED_STATE), else the rows;
//   - the grid's ready is the first read (ready), and its footer's reload and a
//     poll run it again;
//   - a failed re-read keeps the rows already there and says why (error): one
//     policy for all six lists. The rows are kept only for the filter they were
//     read for: a read that fails for another filter (a filter change, or a
//     reload or poll that overtook it) leaves none, since the rows of another
//     filter are not the answer;
//   - an answer that arrives after a newer read was begun (a slow response for
//     the old filter, an old poll) is dropped, so the list shows what was
//     asked for last;
//   - a poll (ready called with no grid) does not begin a read while one is
//     still out: it would supersede it, and a read slower than the poll
//     interval would then never land while the list claimed to be live. It
//     skips (and, with pollFails, throws, so the grid's Live pill reads
//     Reconnecting: two ticks without an answer); the slow answer, when it
//     lands, is applied. A reload (the grid given) and a filter change do go out;
//   - the filter, when it changes, reads again (the grid's own ready did the
//     first read, so the first value reads nothing).
// fetchRows(filter, cursor) answers the rows, or { rows, next, warning }: `next`
// is a cursor for loadMore (its rows are added to the list), `warning` a note on
// an answer that is partly there (a failed read clears it). `current` is whether
// the rows are for the filter now chosen (false while a read for a new filter is
// out): loadMore sends nothing until then, since the cursor is the old
// filter's.
export const useGridRows = (fetchRows, { filter = '', pollFails = false } = {}) => {
  const [state, setState] = useState({ rows: undefined, rowsFilter: undefined, error: null, warning: null, next: '' })
  const sequence = useRef(0)
  const pending = useRef(0)
  const fetchRef = useRef(fetchRows)
  fetchRef.current = fetchRows
  const filterRef = useRef(filter)
  filterRef.current = filter

  // One read. It answers the failure, or null (and null for an answer that was
  // dropped as out of date).
  const read = useCallback(async ({ cursor } = {}) => {
    const mine = ++sequence.current
    const used = filterRef.current
    pending.current = mine
    try {
      const answer = await fetchRef.current(used, cursor)
      if (mine !== sequence.current) return null
      const { rows, next = '', warning = null } = Array.isArray(answer) ? { rows: answer } : answer
      setState(prior => ({
        rows: cursor ? [...(prior.rowsFilter === used && Array.isArray(prior.rows) ? prior.rows : []), ...rows] : rows,
        rowsFilter: used,
        error: null,
        warning,
        next
      }))
      return null
    } catch (failure) {
      if (mine !== sequence.current) return null
      setState(prior => {
        const keep = prior.rowsFilter === used && Array.isArray(prior.rows)
        return { ...prior, rows: keep ? prior.rows : null, error: failure, warning: null, next: keep ? prior.next : '' }
      })
      return failure
    } finally {
      if (pending.current === mine) pending.current = 0
    }
  }, [])

  const ready = useCallback(
    async grid => {
      if (!grid && pending.current) {
        if (pollFails) throw new Error('The last read has not answered yet.')
        return
      }
      const failure = await read()
      if (failure && pollFails && !grid) throw failure
    },
    [read, pollFails]
  )

  const current = state.rowsFilter === filter

  const loadMore = useCallback(async () => {
    if (state.rowsFilter !== filterRef.current || !state.next) return null
    return read({ cursor: state.next })
  }, [read, state.next, state.rowsFilter])

  const mounted = useRef(false)
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    read()
  }, [filter, read])

  return { ...state, current, read, ready, loadMore }
}

// An action in a grid row, a real button styled like the confirm dialog's:
// the outline for a neutral action, the red outline for one that removes or
// ends something (sso-workspace.scss). The actions cell is a flex row as tall
// as the grid row, so a button centres itself in it rather than stretching.
export const RowAction = ({ danger, children, ...props }) =>
  <button type="button" className={`btn btn-sm align-self-center mx-1 ${danger ? 'sw-btn-outline-danger' : 'sw-btn'}`} {...props}>{children}</button>

// How long focus is held on a row after its action (returnFocusToRow), unless
// the person acts first: long enough for the grid to draw the rows again after
// the reload the action awaited, short enough not to outstay it.
export const FOCUS_HOLD_MS = 2000

// What to focus among a row's actions (`scope` holds the grid): its ⋮, else the
// first enabled button in its action cell, else the cell itself (the action is
// gone: a revoked key, an archived link). The cell is the one RowActions marked
// with the row's id (data-lms-row): the grid's own row ids are positions, given
// anew when a reload draws the rows again. Null when the row is not drawn.
const rowActionTarget = (scope, id) => {
  const marker = Array.from(scope.querySelectorAll('[data-lms-row]')).find(node => node.getAttribute('data-lms-row') === id)
  const cell = marker && marker.closest('.ag-cell')
  if (!cell) return null
  return cell.querySelector('button[aria-haspopup="menu"]') || cell.querySelector('button:not([disabled])') || cell
}

// After a row's action, focus goes back to that row's actions (rowActionTarget)
// rather than falling to the page: the grid draws the row anew when its list
// reloads, so the row is found again by its id in `root` (the grid, or the page
// when the grid itself was drawn anew). Call it once the action and the reload
// it awaited have answered. For FOCUS_HOLD_MS after, each change to the page (a
// redraw, a confirm dialog closing) looks at where focus is: on nothing, or on
// a bare action cell (AG Grid focuses the cell at the old position as it draws
// the rows again), it is put on the row's actions. Anything else is left where
// it is, and nothing listens to focus itself, so a screen reader moving focus
// onto a cell is never fought. It lets go at the person's next key or press,
// or when the time is up. Returns the function that lets go.
export const returnFocusToRow = (root, rowId, { hold = FOCUS_HOLD_MS } = {}) => {
  if (rowId === undefined || rowId === null) return () => {}
  const id = String(rowId)
  const place = () => {
    const target = rowActionTarget(root && root.isConnected ? root : document, id)
    const active = document.activeElement
    if (!target || active === target) return
    const lost = !active || active === document.body || !active.isConnected
    const bareActionCell = Boolean(active && active.matches && active.matches('.ag-cell[col-id="menu"]'))
    if (lost || bareActionCell) target.focus()
  }
  const observer = new MutationObserver(place)
  let timer = null
  const stop = () => {
    observer.disconnect()
    clearTimeout(timer)
    document.removeEventListener('keydown', stop, true)
    document.removeEventListener('pointerdown', stop, true)
  }
  observer.observe(document.body, { childList: true, subtree: true })
  document.addEventListener('keydown', stop, true)
  document.addEventListener('pointerdown', stop, true)
  timer = setTimeout(stop, hold)
  place()
  return stop
}

// A row's actions: [{ label, name, danger, onClick, show }]. Each is a
// RowAction, named for its row for assistive tech ("Revoke Acme key": `name`),
// and on a phone they fold into one ⋮ menu, named for the row too (`rowName`:
// "More actions for Acme key"). None shown, none drawn (but the marker below).
//
// Focus comes back to the row (`rowId`, the row's own id) after an action: once
// an onClick that answers a promise (a pause, and the reload it awaits) has
// answered, and, for an action that asks first, when its dialog is done:
// onClick is handed `returnFocus` to call then (on Cancel at once; once done,
// after the reload). With a rowId the actions mark their cell with it (a hidden
// data-lms-row), even when none is shown, so focus finds the row again.
export const RowActions = ({ actions, rowName, rowId }) => {
  const mobile = useIsMobile()
  const shown = actions.filter(action => action.show !== false)
  const marker = rowId === undefined || rowId === null ? null : <span hidden data-lms-row={String(rowId)} />
  if (!shown.length) return marker
  // `from`: the button or ⋮ the action was chosen from, in the grid
  const run = (action, from) => {
    const root = from ? from.closest('.lms-grid') : null
    const returnFocus = () => returnFocusToRow(root, rowId)
    const answer = action.onClick(returnFocus)
    if (answer && typeof answer.then === 'function') answer.then(returnFocus, returnFocus)
  }
  if (mobile) {
    return (
      <React.Fragment>
        {marker}
        <RowMenu label={rowName ? `More actions for ${rowName}` : 'More actions'} actions={shown} onChoose={run} />
      </React.Fragment>
    )
  }
  return (
    <React.Fragment>
      {marker}
      {shown.map(action => (
        <RowAction key={action.label} danger={action.danger} aria-label={action.name} onClick={event => run(action, event.currentTarget)}>
          {action.label}
        </RowAction>
      ))}
    </React.Fragment>
  )
}

// The phone's ⋮ for a row: the table builder's own popup (Floating) and button
// look, with the row's name on the button. (The table builder's Menu names its
// ⋮ "More actions" for every row, and wraps it in blocks that leave it at the
// top of a two-line row.) It is a real menu: the items are buttons
// (role=menuitem); opening focuses the first; the arrow keys, Home and End move
// between them (round the ends); Enter and Space activate (a button's own); and
// Escape, Tab or choosing one closes it with focus back on the ⋮. Choosing one
// hands it to onChoose(action, the ⋮).
const RowMenu = ({ label, actions, onChoose }) => {
  const [open, setOpen] = useState(false)
  const toggle = useRef(null)
  const menu = useRef(null)
  const items = () => (menu.current ? Array.from(menu.current.querySelectorAll('[role="menuitem"]')) : [])

  // The menu is drawn in a portal a render after it opens, so its ref is what
  // knows it has arrived: opening puts focus on the first item.
  const attach = useCallback(node => {
    menu.current = node
    if (node) node.querySelector('[role="menuitem"]')?.focus()
  }, [])

  const close = ({ refocus } = {}) => {
    setOpen(false)
    if (refocus && toggle.current) toggle.current.focus()
  }

  // The floating layer closes it (Escape, a press outside, a click on the ⋮);
  // when focus was inside the menu it goes back to the ⋮, not to the page.
  const handleOpenChange = next => {
    const inside = menu.current && menu.current.contains(document.activeElement)
    setOpen(next)
    if (!next && inside && toggle.current) toggle.current.focus()
  }

  const handleKeyDown = event => {
    const list = items()
    const at = list.indexOf(document.activeElement)
    const move = index => {
      event.preventDefault()
      list[(index + list.length) % list.length].focus()
    }
    switch (event.key) {
      case 'ArrowDown': return move(at + 1)
      case 'ArrowUp': return move(at < 0 ? list.length - 1 : at - 1)
      case 'Home': return move(0)
      case 'End': return move(list.length - 1)
      case 'Escape':
        event.preventDefault()
        return close({ refocus: true })
      case 'Tab':
        return close({ refocus: true })
      default:
    }
  }

  const choose = action => {
    close({ refocus: true })
    onChoose(action, toggle.current)
  }

  const content = (
    <div>
      <div ref={attach} role="menu" aria-label={label} className="list-group border" onKeyDown={handleKeyDown}>
        {actions.map(action => (
          <button
            key={action.label}
            type="button"
            role="menuitem"
            tabIndex={-1}
            className="list-group-item list-group-item-action border-0 text-start text-nowrap"
            onClick={() => choose(action)}>
            <small>{action.label}</small>
          </button>
        ))}
      </div>
    </div>
  )
  return (
    <Floating content={content} isOpen={open} onOpenChange={handleOpenChange}>
      <button ref={toggle} type="button" aria-label={label} aria-haspopup="menu" aria-expanded={open} className="btn border btn-sm">
        <IconMenu size="lg" />
      </button>
    </Floating>
  )
}

// A cell's text that is cut with an ellipsis where its column ends
// (lms-admin.scss .lms-clip), the full text in `title`. It takes the width the
// column has. `size` bounds only what the table builder measures for the
// column's floor (s 9rem, m 12rem, l 16rem, the default): the table builder
// sizes a column to its widest cell, and an unbounded name or address would
// push the columns after it off the page beside the sidebar.
export const Clip = ({ children, className, size = 'l', title, ...props }) =>
  <span className={`lms-clip lms-clip-${size}${className ? ` ${className}` : ''}`} title={title === undefined && typeof children === 'string' ? children : title} {...props}>{children}</span>

// A name in a grid cell: the monogram of the LMS it belongs to (logoFor), the
// name (a link to `to`, when there is one) and a subline: `sub` (text), or
// `line` (a node of your own). On a phone the subline is `phone` instead (a
// status pill and a word), so the row says what it is and how it stands
// without a column to scroll to.
export const NameCell = ({ logoName, name, to, sub, line: given, phone, size = 'l' }) => {
  const logo = logoName === undefined ? null : logoFor(logoName)
  const line = phone || given || (sub ? <Clip size={size} className="sw-cell-sub">{sub}</Clip> : null)
  return (
    <span className="d-inline-flex align-items-center gap-2">
      {logo && <span className={`conn-logo ${logo.className}`}>{logo.text}</span>}
      <span className="min-w-0">
        {to
          ? <Link to={to} className={`fw-semibold lms-clip lms-clip-${size}`} title={name}>{name}</Link>
          : <Clip size={size} className="fw-semibold">{name}</Clip>}
        {line}
      </span>
    </span>
  )
}

// The LMS connection a row belongs to (links and sessions): its monogram and
// name. The row holds connectionName and connectionPlatform.
export const connectionCell = cell =>
  cell.data ? <NameCell logoName={cell.data.connectionPlatform} name={cell.data.connectionName} size="s" /> : ''

// The second line of a phone cell: a pill (or pills), then a word that is cut
// when long (`title` has it whole when it is not plain text).
export const PhoneLine = ({ pill, title, children }) =>
  <span className="lms-phone-line">
    {pill}
    {children && <Clip size="s" className="sw-cell-sub" title={title}>{children}</Clip>}
  </span>

// How long ago an instant was, in the viewer's own zone (local-time.js
// agoText: "5 minutes ago", "yesterday", "Sep 28"), or '' for no time. A grid
// row holds this text so the grid sorts, filters and exports what it shows.
export const agoOf = value => (value ? agoText(value) : '')

const FRESH_MS = 5 * 60 * 1000
const STALE_MS = 24 * 60 * 60 * 1000

// An instant as how long ago it was, with the precise UTC instant on hover.
// The text follows the page's minute clock, so a grid that is not polled still
// reads "5 minutes ago" a minute later. `cue` adds the freshness cue: a green
// dot under five minutes, amber from a day. `empty` is shown for no time.
export const AgoCell = ({ value, cue, empty = '—' }) => {
  const now = useMinuteClock()
  const ms = value ? Date.parse(value) : NaN
  const text = Number.isFinite(ms) ? agoText(value, { now }) : ''
  if (!text) return <span className="sw-hint">{empty}</span>
  const age = now - ms
  const freshness = cue ? (age < FRESH_MS ? ' fresh' : age >= STALE_MS ? ' stale' : '') : ''
  return <time className={`lms-ago${freshness}`} dateTime={new Date(ms).toISOString()} title={utcText(value)}>{text}</time>
}

// A grid column's own sort order from a numeric field of the row, for a cell
// that shows words (ColumnSelector/ColumnFormat pass a comparator through).
export const byField = key => (a, b, nodeA, nodeB) => {
  const x = nodeA && nodeA.data ? nodeA.data[key] : 0
  const y = nodeB && nodeB.data ? nodeB.data[key] : 0
  return x === y ? 0 : x < y ? -1 : 1
}

// A column's own sort order from a text field of the row (a cell whose field
// holds more than its name so that the grid's filter finds it all: the name is
// the sort).
export const byText = key => (a, b, nodeA, nodeB) => {
  const x = nodeA && nodeA.data ? nodeA.data[key] : ''
  const y = nodeB && nodeB.data ? nodeB.data[key] : ''
  return String(x || '').localeCompare(String(y || ''))
}

// An instant as a CSV export writes it: the precise UTC time, the same for
// every reader (a grid cell says "5 minutes ago", which a spreadsheet cannot
// sort or date). '' for no time.
export const exportTime = value => (value ? utcText(value) : '')

// The instant of an ISO time as a number (0 for none), for byField.
export const msOf = value => {
  const ms = value ? Date.parse(value) : NaN
  return Number.isFinite(ms) ? ms : 0
}
